import React, { useState, useEffect } from 'react';
import { ArrowRight, Mail, Bell, Clock, Sparkles, Star, Users, Zap, Shield, Eye, EyeOff, Lock, User, LogOut, BarChart3, Settings, Database } from 'lucide-react';
import { supabase } from '../lib/supabase';
import WaitlistForm from './WaitlistForm';
import WaitlistStats from './WaitlistStats';

interface ComingSoonLandingProps {
  onBackToApp?: () => void;
}

interface AdminUser {
  id: string;
  email: string;
  role: string;
}

export default function ComingSoonLanding({ onBackToApp }: ComingSoonLandingProps) {
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [currentFeature, setCurrentFeature] = useState(0);

  const features = [
    { icon: Zap, title: 'Lightning Fast', desc: 'Optimized for speed and performance' },
    { icon: Shield, title: 'Secure', desc: 'Enterprise-grade security built-in' },
    { icon: Users, title: 'Community', desc: 'Join thousands of early adopters' },
    { icon: Sparkles, title: 'Innovation', desc: 'Cutting-edge technology stack' }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentFeature((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [features.length]);

  useEffect(() => {
    // Check if user is already logged in
    const checkUser = async () => {
      if (!supabase) return;
      
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setAdminUser({
          id: user.id,
          email: user.email || '',
          role: 'admin'
        });
      }
    };

    checkUser();

    // Listen for auth changes
    if (supabase) {
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        if (session?.user) {
          setAdminUser({
            id: session.user.id,
            email: session.user.email || '',
            role: 'admin'
          });
        } else {
          setAdminUser(null);
        }
      });

      return () => subscription.unsubscribe();
    }
  }, []);

  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) {
      setLoginError('Authentication service not available');
      return;
    }

    setIsLoading(true);
    setLoginError('');

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: loginForm.email,
        password: loginForm.password,
      });

      if (error) {
        setLoginError(error.message);
      } else if (data.user) {
        setAdminUser({
          id: data.user.id,
          email: data.user.email || '',
          role: 'admin'
        });
        setShowAdminLogin(false);
        setLoginForm({ email: '', password: '' });
      }
    } catch (err) {
      setLoginError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdminLogout = async () => {
    if (!supabase) return;
    
    await supabase.auth.signOut();
    setAdminUser(null);
  };

  const AdminDashboard = () => (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-md z-50 flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-gray-900 to-black border border-pink-500/20 rounded-2xl p-8 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">Admin Dashboard</h2>
            <p className="text-gray-400">Welcome back, {adminUser?.email}</p>
          </div>
          <button
            onClick={handleAdminLogout}
            className="flex items-center space-x-2 bg-red-500/20 text-red-400 px-4 py-2 rounded-lg hover:bg-red-500/30 transition-colors"
          >
            <LogOut className="h-4 w-4" />
            <span>Logout</span>
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border border-blue-500/30 rounded-xl p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Users className="h-8 w-8 text-blue-400" />
              <div>
                <h3 className="text-white font-semibold">Waitlist</h3>
                <p className="text-gray-400 text-sm">Total signups</p>
              </div>
            </div>
            <WaitlistStats />
          </div>

          <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-xl p-6">
            <div className="flex items-center space-x-3 mb-4">
              <BarChart3 className="h-8 w-8 text-green-400" />
              <div>
                <h3 className="text-white font-semibold">Analytics</h3>
                <p className="text-gray-400 text-sm">Site metrics</p>
              </div>
            </div>
            <div className="text-2xl font-bold text-white">98.5%</div>
            <p className="text-green-400 text-sm">Uptime</p>
          </div>

          <div className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 rounded-xl p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Database className="h-8 w-8 text-purple-400" />
              <div>
                <h3 className="text-white font-semibold">Database</h3>
                <p className="text-gray-400 text-sm">System status</p>
              </div>
            </div>
            <div className="text-2xl font-bold text-white">Online</div>
            <p className="text-purple-400 text-sm">All systems operational</p>
          </div>
        </div>

        <div className="bg-gray-800/50 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4 flex items-center space-x-2">
            <Settings className="h-5 w-5" />
            <span>Quick Actions</span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <button className="bg-blue-500/20 text-blue-400 px-4 py-3 rounded-lg hover:bg-blue-500/30 transition-colors text-sm">
              Export Waitlist
            </button>
            <button className="bg-green-500/20 text-green-400 px-4 py-3 rounded-lg hover:bg-green-500/30 transition-colors text-sm">
              Send Update
            </button>
            <button className="bg-purple-500/20 text-purple-400 px-4 py-3 rounded-lg hover:bg-purple-500/30 transition-colors text-sm">
              View Analytics
            </button>
            <button className="bg-orange-500/20 text-orange-400 px-4 py-3 rounded-lg hover:bg-orange-500/30 transition-colors text-sm">
              System Logs
            </button>
          </div>
        </div>

        {/* Admin-only access to main app */}
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-6 mt-6">
          <h3 className="text-yellow-400 font-semibold mb-4 flex items-center space-x-2">
            <Lock className="h-5 w-5" />
            <span>Admin Access</span>
          </h3>
          <p className="text-gray-300 mb-4 text-sm">
            Access the full application (hidden from public users)
          </p>
          <button
            onClick={onBackToApp}
            className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg hover:shadow-pink-500/25 transition-all duration-300 flex items-center space-x-2"
          >
            <ArrowRight className="h-4 w-4" />
            <span>Access Full App</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <style jsx>{`
        .font-inter { font-family: 'Inter', sans-serif; }
        .font-poppins { font-family: 'Poppins', sans-serif; }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes pulse-glow {
          0%, 100% { 
            box-shadow: 0 0 40px rgba(236, 72, 153, 0.4);
          }
          50% { 
            box-shadow: 0 0 60px rgba(236, 72, 153, 0.8);
          }
        }
        
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        @keyframes morphBackground {
          0%, 100% { 
            background: linear-gradient(135deg, #1e1b4b, #581c87, #be185d);
          }
          33% { 
            background: linear-gradient(135deg, #581c87, #be185d, #0c4a6e);
          }
          66% { 
            background: linear-gradient(135deg, #be185d, #0c4a6e, #1e1b4b);
          }
        }
        
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-pulse-glow { animation: pulse-glow 3s ease-in-out infinite; }
        .animate-slide-up { animation: slideUp 0.8s ease-out forwards; }
        .animate-morph { animation: morphBackground 12s ease-in-out infinite; }
        
        .glass-morphism {
          background: rgba(255, 255, 255, 0.08);
          backdrop-filter: blur(24px);
          border: 1px solid rgba(255, 255, 255, 0.12);
        }
        
        .premium-card {
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
          backdrop-filter: blur(30px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }
        
        .gradient-text {
          background: linear-gradient(135deg, #ec4899, #8b5cf6, #06b6d4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        
        .hover-lift:hover {
          transform: translateY(-4px);
        }
        
        .shimmer-effect {
          position: relative;
          overflow: hidden;
        }
        
        .shimmer-effect::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
          animation: shimmer 3s infinite;
        }
      `}</style>

      <div className="min-h-screen animate-morph relative overflow-hidden">
        {/* Admin Login Button */}
        <button
          onClick={() => setShowAdminLogin(true)}
          className="fixed top-6 right-6 z-40 w-8 h-8 bg-gray-800/50 backdrop-blur-md border border-gray-600/30 rounded-full flex items-center justify-center text-gray-400 hover:text-white hover:bg-gray-700/50 transition-all duration-300"
          title="Admin Login"
        >
          <Lock className="h-4 w-4" />
        </button>

        {/* Admin User Indicator */}
        {adminUser && (
          <button
            onClick={() => setShowAdminLogin(true)}
            className="fixed top-6 right-6 z-40 glass-morphism text-green-400 px-4 py-2 rounded-full font-medium hover:bg-green-500/10 transition-all duration-300 flex items-center space-x-2 text-sm"
          >
            <User className="h-4 w-4" />
            <span>Admin</span>
          </button>
        )}

        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-2 h-2 bg-pink-400 rounded-full animate-pulse"></div>
          <div className="absolute top-40 right-20 w-3 h-3 bg-purple-400 rounded-full animate-bounce"></div>
          <div className="absolute bottom-40 left-20 w-1 h-1 bg-cyan-400 rounded-full animate-ping"></div>
          <div className="absolute top-1/3 right-1/3 w-4 h-4 bg-gradient-to-r from-pink-400/30 to-purple-400/30 rounded-full blur-sm animate-pulse"></div>
        </div>

        <div className="relative z-10 min-h-screen flex items-center justify-center px-4">
          <div className="max-w-4xl mx-auto text-center">
            {/* Main Content */}
            <div className="premium-card rounded-3xl p-12 lg:p-16 shimmer-effect animate-slide-up">
              {/* Logo */}
              <div className="relative mb-12">
                <div className="w-32 h-32 lg:w-40 lg:h-40 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center mx-auto animate-pulse-glow">
                  <img 
                    src="/logo transparent with tagline.png" 
                    alt="UNDRESSING Logo" 
                    className="h-20 lg:h-24 w-auto object-contain animate-float"
                  />
                </div>
                
                {/* Floating Elements */}
                <div className="absolute -top-6 -right-6 w-12 h-12 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center animate-float" style={{ animationDelay: '1s' }}>
                  <Sparkles className="h-6 w-6 text-white" />
                </div>
                
                <div className="absolute -bottom-6 -left-6 w-12 h-12 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center animate-float" style={{ animationDelay: '2s' }}>
                  <Star className="h-6 w-6 text-white" />
                </div>
              </div>

              {/* Title */}
              <h1 className="font-poppins font-black text-5xl lg:text-7xl text-white mb-6">
                Something Amazing
                <span className="gradient-text block">Is Coming</span>
              </h1>

              {/* Subtitle */}
              <p className="font-inter text-xl lg:text-2xl text-slate-300 leading-relaxed max-w-2xl mx-auto mb-12">
                We're crafting an extraordinary experience that will revolutionize the way you think about flavor. 
                Get ready to strip the bland and drip the bold.
              </p>

              {/* Countdown Timer */}
              <div className="grid grid-cols-4 gap-4 mb-12 max-w-md mx-auto">
                {[
                  { value: '30', label: 'Days' },
                  { value: '12', label: 'Hours' },
                  { value: '45', label: 'Minutes' },
                  { value: '23', label: 'Seconds' }
                ].map((time, index) => (
                  <div key={index} className="glass-morphism rounded-xl p-4">
                    <div className="text-2xl lg:text-3xl font-bold text-white">{time.value}</div>
                    <div className="text-slate-400 text-sm">{time.label}</div>
                  </div>
                ))}
              </div>

              {/* Features Carousel */}
              <div className="mb-12">
                <div className="glass-morphism rounded-2xl p-8 min-h-[120px] flex items-center justify-center">
                  <div className="text-center">
                    <div className={`w-16 h-16 bg-gradient-to-r ${
                      currentFeature === 0 ? 'from-yellow-500 to-orange-500' :
                      currentFeature === 1 ? 'from-green-500 to-emerald-500' :
                      currentFeature === 2 ? 'from-blue-500 to-cyan-500' :
                      'from-purple-500 to-pink-500'
                    } rounded-xl flex items-center justify-center mx-auto mb-4`}>
                      {React.createElement(features[currentFeature].icon, { className: "h-8 w-8 text-white" })}
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{features[currentFeature].title}</h3>
                    <p className="text-slate-300">{features[currentFeature].desc}</p>
                  </div>
                </div>
                
                {/* Feature Indicators */}
                <div className="flex justify-center space-x-2 mt-6">
                  {features.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentFeature(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        index === currentFeature ? 'bg-pink-500 scale-125' : 'bg-gray-600'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Waitlist Form */}
              <div className="mb-8">
                <WaitlistForm 
                  source="coming-soon-landing" 
                  className="max-w-lg mx-auto"
                />
              </div>

              {/* Social Proof */}
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 text-slate-400">
                <div className="flex items-center space-x-2">
                  <div className="flex -space-x-2">
                    {[1,2,3,4].map((i) => (
                      <div key={i} className="w-8 h-8 bg-gradient-to-r from-pink-400 to-purple-500 rounded-full border-2 border-slate-900"></div>
                    ))}
                  </div>
                  <span className="text-sm">Join 1,200+ early adopters</span>
                </div>
                <div className="flex items-center space-x-1">
                  {[1,2,3,4,5].map((i) => (
                    <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />
                  ))}
                  <span className="text-sm ml-2">Rated 5/5 by beta users</span>
                </div>
              </div>
            </div>

            {/* Bottom Message */}
            <div className="mt-12 text-center">
              <p className="font-inter text-slate-400">
                Follow our journey and be the first to experience the future of flavor.
              </p>
            </div>
          </div>
        </div>

        {/* Admin Login Modal */}
        {showAdminLogin && !adminUser && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4">
            <div className="bg-gradient-to-br from-gray-900 to-black border border-pink-500/20 rounded-2xl p-8 max-w-md w-full">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Admin Login</h2>
                <p className="text-gray-400">Access the admin dashboard</p>
              </div>

              <form onSubmit={handleAdminLogin} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="email"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500"
                      placeholder="admin@example.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      className="w-full pl-10 pr-12 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-pink-500 focus:ring-1 focus:ring-pink-500"
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {loginError && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                    <p className="text-red-400 text-sm">{loginError}</p>
                  </div>
                )}

                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowAdminLogin(false)}
                    className="flex-1 px-6 py-3 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg hover:shadow-pink-500/25 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Signing In...' : 'Sign In'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Admin Dashboard */}
        {adminUser && showAdminLogin && <AdminDashboard />}
      </div>
    </>
  );
}